import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  LogIn,
  BadgeCheck,
  XCircle,
  Trash2,
  AlertTriangle,
  Building2,
  Users,
  Banknote,
  Phone,
  Mail,
  Calendar,
  Clock,
  Ban,
  RotateCcw,
  ScrollText,
} from "lucide-react";
import { api } from "../../utils/api";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { useAuth } from "../../context/AuthContext";
import TypedConfirmDialog from "../../components/TypedConfirmDialog";
import WaiveBillingDialog from "../../components/WaiveBillingDialog";
import ConfirmDialog from "../../components/ConfirmDialog";

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return "—";
  }
}

function fmtLastLogin(iso) {
  if (!iso) return "Never";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return "Never";
  }
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return "—";
  }
}

function fmtKES(n) {
  return new Intl.NumberFormat("en-KE", {
    style: "currency",
    currency: "KES",
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="card p-4">
      <div className="mb-1 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {Icon && <Icon className="h-3.5 w-3.5" />}
        {label}
      </div>
      <div className="text-xl font-semibold text-gray-900 dark:text-white">
        {value}
      </div>
    </div>
  );
}

function Field({ label, children, icon: Icon }) {
  return (
    <div className="min-w-0">
      <div className="mb-0.5 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {Icon && <Icon className="h-3.5 w-3.5" />}
        {label}
      </div>
      <div className="break-words text-sm text-gray-900 dark:text-white">
        {children || <span className="text-gray-400">—</span>}
      </div>
    </div>
  );
}

export default function AdminUserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { impersonate } = useAdminAuth();
  const { reload } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // action state
  const [entering, setEntering] = useState(false);

  // waive billing dialog
  const [waiveOpen, setWaiveOpen] = useState(false);
  const [waiveLoading, setWaiveLoading] = useState(false);
  const [confirmUnwaive, setConfirmUnwaive] = useState(false);

  // delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // deactivate dialog
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [deactivateReason, setDeactivateReason] = useState("");
  const [deactivating, setDeactivating] = useState(false);

  // reactivate confirm
  const [confirmReactivate, setConfirmReactivate] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get(`/admin/users/${id}`)
      .then((res) => setData(res))
      .catch((err) => setError(err.message || "Failed to load user"))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (deactivateOpen) setDeactivateReason("");
  }, [deactivateOpen]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate("/admin/users")}
          className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4" /> Back to users
        </button>
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error || "User not found"}
        </div>
      </div>
    );
  }

  const user = data.user;
  const recent = data.recentEvents || [];

  const onEnterDashboard = async () => {
    setEntering(true);
    try {
      await impersonate(user.id);
      await reload();
      navigate("/app/dashboard");
    } catch (err) {
      setError(err.message || "Failed to start impersonation");
      setEntering(false);
    }
  };

  const onConfirmWaive = async (reason) => {
    setWaiveLoading(true);
    try {
      await api.post(`/admin/users/${user.id}/waive-billing`, {
        waived: true,
        reason: reason || null,
      });
      await load();
      setWaiveOpen(false);
    } catch (err) {
      setError(err.message || "Failed to waive billing");
    } finally {
      setWaiveLoading(false);
    }
  };

  const onConfirmUnwaive = async () => {
    setWaiveLoading(true);
    try {
      await api.post(`/admin/users/${user.id}/waive-billing`, {
        waived: false,
      });
      await load();
      setConfirmUnwaive(false);
    } catch (err) {
      setError(err.message || "Failed to un-waive billing");
    } finally {
      setWaiveLoading(false);
    }
  };

  const onConfirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${user.id}`);
      navigate("/admin/users", { replace: true });
    } catch (err) {
      setError(err.message || "Failed to delete user");
      setDeleting(false);
      setDeleteOpen(false);
    }
  };

  const onConfirmDeactivate = async () => {
    setDeactivating(true);
    try {
      await api.post(`/admin/users/${user.id}/deactivate`, {
        reason: deactivateReason.trim(),
      });
      await load();
      setDeactivateOpen(false);
    } catch (err) {
      setError(err.message || "Failed to deactivate user");
    } finally {
      setDeactivating(false);
    }
  };

  const onConfirmReactivate = async () => {
    setReactivating(true);
    try {
      await api.post(`/admin/users/${user.id}/reactivate`);
      await load();
      setConfirmReactivate(false);
    } catch (err) {
      setError(err.message || "Failed to reactivate user");
    } finally {
      setReactivating(false);
    }
  };

  const isDeactivated = !!user.deactivated;

  return (
    <div className="space-y-5">
      <button
        onClick={() => navigate("/admin/users")}
        className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
      >
        <ArrowLeft className="h-4 w-4" /> Back to users
      </button>

      {/* Header */}
      <div className="card p-4 sm:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <h1 className="break-words text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
              {user.fullName || "(no name)"}
            </h1>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
              <span className="flex items-center gap-1 break-all">
                <Mail className="h-3.5 w-3.5 flex-none" /> {user.email}
              </span>
              <span className="flex items-center gap-1">
                <Phone className="h-3.5 w-3.5 flex-none" />{" "}
                {user.phone?.e164 || user.phone || "no phone"}
              </span>
              <span className="font-mono text-[11px]">
                #{user.accountNumber || "—"}
              </span>
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                {user.authMethod}
              </span>
              {isDeactivated && (
                <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700 ring-1 ring-red-600/20">
                  <Ban className="h-3 w-3" /> Deactivated
                </span>
              )}
              {user.billingWaived && (
                <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-700 ring-1 ring-green-600/20">
                  <BadgeCheck className="h-3 w-3" /> Billing waived
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={onEnterDashboard}
              className="btn-primary"
              disabled={entering || isDeactivated}
              title={isDeactivated ? "Cannot impersonate a deactivated user" : undefined}
            >
              {entering ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <LogIn className="h-4 w-4" />
              )}
              Enter dashboard
            </button>
            {user.billingWaived ? (
              <button
                onClick={() => setConfirmUnwaive(true)}
                className="btn-secondary"
                disabled={waiveLoading}
              >
                <XCircle className="h-4 w-4" />
                Un-waive billing
              </button>
            ) : (
              <button
                onClick={() => setWaiveOpen(true)}
                className="btn-secondary"
                disabled={waiveLoading}
              >
                <BadgeCheck className="h-4 w-4" />
                Waive billing
              </button>
            )}
            {isDeactivated ? (
              <button
                onClick={() => setConfirmReactivate(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300"
              >
                <RotateCcw className="h-4 w-4" /> Reactivate
              </button>
            ) : (
              <button
                onClick={() => setDeactivateOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300"
              >
                <Ban className="h-4 w-4" /> Deactivate
              </button>
            )}
            <button
              onClick={() => setDeleteOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-100 dark:border-red-800 dark:bg-red-950 dark:text-red-400"
            >
              <Trash2 className="h-4 w-4" /> Delete
            </button>
          </div>
        </div>
        {isDeactivated && user.deactivatedReason && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
            <span className="font-semibold">Deactivation reason:</span>{" "}
            {user.deactivatedReason}
            <div className="mt-1 text-[11px] text-amber-700 dark:text-amber-400">
              By {user.deactivatedBy || "admin"} on{" "}
              {fmtDate(user.deactivatedAt)}
            </div>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <Stat label="Properties" value={user.propertyCount ?? 0} icon={Building2} />
        <Stat label="Active tenants" value={user.activeTenantCount ?? 0} icon={Users} />
        <Stat label="Total tenants" value={user.totalTenantCount ?? 0} icon={Users} />
        <Stat label="Total revenue" value={fmtKES(user.totalRevenue)} icon={Banknote} />
      </div>

      {/* Details grid */}
      <div className="grid gap-5 lg:grid-cols-2">
        <div className="card space-y-4 p-4 sm:p-6">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Account details
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Created" icon={Calendar}>
              {fmtDateTime(user.createdAt)}
            </Field>
            <Field label="Last login" icon={Clock}>
              {fmtLastLogin(user.lastLoginAt)}
            </Field>
            <Field label="Email verified" icon={Mail}>
              {user.emailVerified ? "Yes" : "No"}
            </Field>
            <Field label="Phone verified" icon={Phone}>
              {user.needsPhone ? "No" : "Yes"}
            </Field>
            <Field label="Onboarded">{user.onboarded ? "Yes" : "No"}</Field>
            <Field label="Auth method">{user.authMethod}</Field>
          </div>
        </div>

        <div className="card space-y-4 p-4 sm:p-6">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Billing
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Current month">
              {user.currentBill?.month || "—"}
            </Field>
            <Field label="Peak tenants">
              {user.billingPeakTenants ?? 0}
            </Field>
            <Field label="Rate / tenant">
              {fmtKES(user.currentBill?.rate)}
            </Field>
            <Field label="Current bill">
              {user.currentBill?.waived ? (
                <span className="text-green-600">Waived (KES 0)</span>
              ) : (
                fmtKES(user.currentBill?.total)
              )}
            </Field>
            {user.billingWaived && (
              <>
                <Field label="Waived at">{fmtDate(user.billingWaivedAt)}</Field>
                <Field label="Waived by">{user.billingWaivedBy}</Field>
                {user.billingWaivedReason && (
                  <div className="sm:col-span-2">
                    <Field label="Reason">{user.billingWaivedReason}</Field>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Recent activity */}
      <div className="card p-4 sm:p-6">
        <div className="mb-4 flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Recent activity
          </h2>
          <Link
            to={`/admin/users/${user.id}/events`}
            className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400"
          >
            <ScrollText className="h-3.5 w-3.5" />
            View all events
          </Link>
        </div>
        {recent.length === 0 ? (
          <div className="text-sm text-gray-500">No recent activity.</div>
        ) : (
          <ul className="space-y-2 text-sm">
            {recent.map((e) => (
              <li
                key={e.id}
                className="flex items-start justify-between gap-3 border-b border-gray-100 pb-2 last:border-0 dark:border-gray-800"
              >
                <div className="min-w-0">
                  <div className="font-medium text-gray-900 dark:text-white">
                    {e.type}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {e.tenantName || e.houseNumber || ""}
                  </div>
                </div>
                <div className="whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                  {fmtDateTime(e.at)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ── Dialogs ────────────────────────────────────────────────────── */}

      <WaiveBillingDialog
        open={waiveOpen}
        onClose={() => !waiveLoading && setWaiveOpen(false)}
        onConfirm={onConfirmWaive}
        targetName={user.fullName || user.email}
        loading={waiveLoading}
      />

      <ConfirmDialog
        open={confirmUnwaive}
        onClose={() => !waiveLoading && setConfirmUnwaive(false)}
        onConfirm={onConfirmUnwaive}
        title="Un-waive billing?"
        description={
          <span>
            <b>{user.fullName || user.email}</b> will start being billed
            again from next month.
          </span>
        }
        confirmLabel={waiveLoading ? "Working…" : "Un-waive"}
        loading={waiveLoading}
      />

      <TypedConfirmDialog
        open={deleteOpen}
        onClose={() => !deleting && setDeleteOpen(false)}
        onConfirm={onConfirmDelete}
        title="Delete user account"
        description={
          <div className="flex items-start gap-2 text-red-700 dark:text-red-400">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
            <span>
              This is <b>irreversible</b>. All of {user.fullName || user.email}'s
              properties, tenants, payments, utility charges, communications,
              and events will be permanently deleted.
            </span>
          </div>
        }
        expectedText={user.email}
        inputPlaceholder={user.email}
        confirmLabel={deleting ? "Deleting…" : "Delete permanently"}
        tone="danger"
        loading={deleting}
      />

      <TypedConfirmDialog
        open={deactivateOpen}
        onClose={() => !deactivating && setDeactivateOpen(false)}
        onConfirm={onConfirmDeactivate}
        title="Deactivate user account"
        description={
          <span>
            <b>{user.fullName || user.email}</b> will be unable to sign in or
            create a new account with this email until you reactivate them.
            An email explaining the deactivation will be sent to them.
          </span>
        }
        expectedText={user.email}
        inputPlaceholder={user.email}
        confirmLabel={deactivating ? "Deactivating…" : "Deactivate"}
        tone="danger"
        loading={deactivating}
        extraValid={deactivateReason.trim().length > 0}
        extra={
          <div>
            <label className="label-base">
              Reason for deactivation <span className="text-red-500">*</span>
            </label>
            <textarea
              value={deactivateReason}
              onChange={(e) => setDeactivateReason(e.target.value)}
              placeholder="This message will be emailed to the user…"
              className="input-base min-h-[96px]"
              disabled={deactivating}
            />
            <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
              Required. Shown to the user in the deactivation email.
            </p>
          </div>
        }
      />

      <ConfirmDialog
        open={confirmReactivate}
        onClose={() => !reactivating && setConfirmReactivate(false)}
        onConfirm={onConfirmReactivate}
        title="Reactivate this account?"
        description={
          <span>
            <b>{user.fullName || user.email}</b> will be able to sign in
            again and resume using Paysafi normally.
          </span>
        }
        confirmLabel={reactivating ? "Reactivating…" : "Reactivate"}
        loading={reactivating}
      />

      {error && (
        <button
          onClick={() => setError("")}
          className="fixed bottom-4 right-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700 shadow-lg hover:bg-red-100"
        >
          {error} (click to dismiss)
        </button>
      )}
    </div>
  );
}
